// seat-selection.component.ts
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ClubsHomePage } from 'src/app/modules/clubs/clubs-home/clubs-home.page';
import { FlightBookingService } from 'src/app/services/flight-booking/flight-booking.service';

interface SegmentSeatMap {
  origin: string;
  destination: string;
  segmentIndex: number;
  legIndex: number;
  seatMap: SeatData[];
}
interface SeatData {
  destination: string;
  itinRef: string;
  maxHeight: string;
  maxWidth: string;
  origin: string;
  amount: string;
  seatAvailability: string;
  seatCategory: string;
  seatCharacterstic: string;
  seatGroup: string;
  seatKey: string;
  seatMessage: string;
  seatName: string;
  seatPosition: string;
  seatRef: string;
  seatReferenceApi: string;
  seatStatus: string;
  seatType: string;
  seatcharacteristics: any;
  segRef: string;
  wingSeat: string;
  xaxis: string | number;
  yaxis: string | number;
  priceDetail: PriceDetail;
}

interface PriceDetail {
  finalPrice: number;
  maxPrice: number;
  totalPrice: number;
  currency: string;
}
@Component({
  selector: 'seat-selection',
  templateUrl: './seat-selection.component.html',
  styleUrls: ['./seat-selection.component.scss'],
})
export class SeatSelectionComponent implements OnInit {
  @Input() seatMapBySegment: SegmentSeatMap[] = [];
  @Input() selectedSeatsBySegment: { [segmentIndex: number]: SeatData[] } = [];
  @Input() numberOfPax: number = 1; // Default to 1 if not provided
  @Output() seatUpdated: EventEmitter<any> = new EventEmitter();
  selectedTab = 'Seat';
  seatMapLoading: boolean = false; // Flag to indicate if seat map is loading
  seatMap: SeatData[] = [];
  selectedSeats: SeatData[] = [];
  seatGrid: (SeatData | null)[][] = [];
  public priceCategories: any;
  // Heat-map gradient from cheapest (green) → premium (red),
  // passing through lime → yellow → orange for a classic price scale.
  private gradientStops = [
    { r: 134, g: 239, b: 172 }, // #86EFAC - green (cheapest)
    { r: 190, g: 242, b: 100 }, // #BEF264 - lime
    { r: 253, g: 224, b: 71 },  // #FDE047 - yellow
    { r: 251, g: 146, b: 60 },  // #FB923C - orange
    { r: 248, g: 113, b: 113 }, // #F87171 - light red
    { r: 239, g: 68, b: 68 },   // #EF4444 - red (premium)
  ];
  public selectedSegmentIndex: number = 0;
  public selectedSeatsPerSegment: {
    [segmentIndex: number]: (SeatData | null)[];
  } = [];
  private nextPassengerIndex: number = 0; // Tracks which passenger gets the next seat selection
  private seatMapLoadingSub: any;
  private seatMapSub: any;
  seatRowNumbers: number[] | null;
  seatColumns: string[];
  activeTooltipSeatKey: string | null = null;
  minSeatxaxis: number = 0;
  maxSeatxaxis: number = 0;

  constructor(private flightBookingService: FlightBookingService) {}

  ngOnInit() {
    this.selectedSeatsPerSegment = this.selectedSeatsBySegment || [];
    // Ensure selectedSeatsPerSegment is initialized for each segment and passenger
    if (this.seatMapBySegment?.length) {
      for (let segIdx = 0; segIdx < this.seatMapBySegment?.length; segIdx++) {
        if (!this.selectedSeatsPerSegment[segIdx]) {
          this.selectedSeatsPerSegment[segIdx] = [];
        }
        for (let paxIdx = 0; paxIdx < this.numberOfPax; paxIdx++) {
          if (!this.selectedSeatsPerSegment[segIdx][paxIdx]) {
            this.selectedSeatsPerSegment[segIdx][paxIdx] = null;
          }
        }
      }
    }
    this.nextPassengerIndex = this.getInitialNextPassengerIndex();
    this.seatMapSub = this.flightBookingService.seatMapData$.subscribe(
      (data) => {
        this.seatMapBySegment = data?.segments || [];
        this.seatMap =
          this.seatMapBySegment?.[this.selectedSegmentIndex]?.seatMap || [];
        if (this.seatMap.length > 0) {
          this.createSeatGrid();
        }
      }
    );
    this.seatMapLoadingSub =
      this.flightBookingService.seatMapLoading$.subscribe(
        (loading: boolean) => {
          this.seatMapLoading = loading;
        }
      );
  }

  ngOnDestroy() {
    if (this.seatMapLoadingSub) {
      this.seatMapLoadingSub.unsubscribe();
    }
    if (this.seatMapSub) {
      this.seatMapSub.unsubscribe();
    }
  }
  createSeatGrid() {
    const xaxisValues = this.seatMap
      .map((seat) => Number(seat?.xaxis))
      .filter((axis) => !isNaN(axis));
    this.minSeatxaxis = xaxisValues.length ? Math.min(...xaxisValues) : 0;
    this.maxSeatxaxis = xaxisValues.length ? Math.max(...xaxisValues) : 0;

    const seatNames = this.seatMap.map((seat) => seat?.seatName);
    this.seatRowNumbers = Array.from(
      new Set(seatNames.map((name) => parseInt(name?.match(/\d+/)?.[0] || '0')))
    )
      .filter((n) => n > 0)
      .sort((a, b) => a - b);
    this.seatColumns = Array.from(
      new Set(seatNames.map((name) => name?.match(/[A-Z]$/)?.[0]))
    )
      .filter(Boolean)
      .sort();

    this.seatGrid = [];

    for (const row of this.seatRowNumbers) {
      const seatRow: (SeatData | null)[] = [];
      for (const col of this.seatColumns) {
        const seatName = `${row}${col}`;
        const seat = this.seatMap.find((s) => s?.seatName === seatName) || null;
        seatRow.push(seat);
      }
      this.seatGrid.push(seatRow);
    }
    const uniqueSeatPrices = Array.from(
      new Set(
        this.seatMap
          .map((seat) => {
            const amount = seat?.priceDetail?.finalPrice;
            const currency = seat?.priceDetail?.currency || '';
            return !isNaN(amount) ? `${amount}_${currency}` : null;
          })
          .filter((val) => val !== null)
      )
    ).sort((a, b) => {
      const [amountA] = a.split('_');
      const [amountB] = b.split('_');
      return Number(amountA) - Number(amountB);
    });

    const gradientColors = this.generateGradientPalette(uniqueSeatPrices.length);
    this.priceCategories = uniqueSeatPrices.map((priceCurrency, index) => {
      const [amount, currency] = priceCurrency.split('_');
      return {
        price: Number(amount),
        currency,
        color: gradientColors[index],
        label: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
        }).format(Number(amount)),
      };
    });
    const referenceRow = this.seatColumns.map((_, colIndex) => {
      return (
        this.seatGrid
          .map((row) => row[colIndex])
          .find((seat) => seat !== null) || null
      );
    });
    if (!referenceRow) return;
    this.seatGrid = this.seatGrid.map((row) =>
      row.map((seat, colIndex) => {
        const refSeat = referenceRow[colIndex];
        if (seat) {
          return {
            ...seat,
            xaxis: seat?.xaxis ? Number(seat.xaxis) : 0,
            yaxis: seat?.yaxis ? Number(seat.yaxis) : 0,
          };
        }
        return {
          xaxis: refSeat?.xaxis ? Number(refSeat.xaxis) : 0,
          yaxis: refSeat?.yaxis ? Number(refSeat.yaxis) : 0,
        } as any;
      })
    );
    const headerRow: (SeatData | null)[] = this.seatColumns.map(
      (col, colIndex) => {
        const refSeat = referenceRow[colIndex];
        return {
          colName: col,
          xaxis: refSeat?.xaxis ? Number(refSeat.xaxis) : 0,
          yaxis: refSeat?.yaxis ? Number(refSeat.yaxis) : 0,
          header: true,
        } as any;
      }
    );

    this.seatGrid.unshift(headerRow);
    this.seatRowNumbers.unshift(null);
  }

  getSeatStyle(seat: SeatData | null): { [key: string]: string } {
    if (!seat) return { background: '#f0f0f0' };
    if (seat.seatAvailability === 'Closed') return { background: '#cccccc' };

    const amount = seat?.priceDetail?.finalPrice;
    if (isNaN(amount)) return { background: '#e0e0e0' };
    const idx = this.priceCategories.findIndex((cat) => cat.price === amount);
    const color = idx !== -1 ? this.priceCategories[idx].color : '#ffffff';
    return { background: color };
  }

  getSeatColor(seat: SeatData | null): string {
    if (!seat) return '#f0f0f0';
    if (this.isSeatSelected(seat)) return '#22C55E';
    if (seat.seatAvailability === 'Closed') return '#cccccc';
    const amount = seat?.priceDetail?.finalPrice;
    if (isNaN(amount)) return '#e0e0e0';
    const idx = this.priceCategories.findIndex(
      (cat: { price: number }) => cat.price === amount
    );
    return idx !== -1 ? this.priceCategories[idx].color : '#ffffff';
  }

  getSeatIconName(seat: SeatData | null): string {
    if (this.isSeatSelected(seat)) return 'FLIGHT_SEAT_SELECTED_ICON';
    if (this.isSeatBooked(seat)) return 'FLIGHT_SEAT_BOOKED_ICON';
    return 'FLIGHT_SEAT_ICON';
  }

  isSeatBooked(seat: SeatData | null): boolean {
    if (!seat) return false;
    const status = seat.seatAvailability;
    return !!status && status === 'Closed';
  }

  selectSeat(seat: SeatData | null) {
    if (!seat || seat.seatAvailability === 'Closed') return;
    if (!this.selectedSeatsPerSegment[this.selectedSegmentIndex]) {
      this.selectedSeatsPerSegment[this.selectedSegmentIndex] = [];
    }
    // Check if the seat is already selected for any passenger
    const alreadySelectedIdx = this.selectedSeatsPerSegment[
      this.selectedSegmentIndex
    ].findIndex((s) => s && s.seatKey === seat.seatKey);
    if (alreadySelectedIdx !== -1) {
      // Deselect: remove the seat for that passenger and set nextPassengerIndex to that passenger
      this.selectedSeatsPerSegment[this.selectedSegmentIndex][
        alreadySelectedIdx
      ] = null;
      this.nextPassengerIndex = alreadySelectedIdx;
      this.seatUpdated.emit({ selectedSeats: this.selectedSeatsPerSegment });
      return;
    }
    // Assign seat to next passenger, replacing previous seat if needed
    this.selectedSeatsPerSegment[this.selectedSegmentIndex][
      this.nextPassengerIndex
    ] = seat;
    // Move to next passenger (cycle)
    this.nextPassengerIndex = (this.nextPassengerIndex + 1) % this.numberOfPax;
    this.seatUpdated.emit({
      selectedSeats: this.selectedSeatsPerSegment,
    });
  }

  onSeatClick(seat: SeatData | null, event: MouseEvent) {
    event.stopPropagation();
    if (!seat) return;
    this.activeTooltipSeatKey = seat.seatKey;
    if (seat.seatAvailability === 'Closed') return;
    this.selectSeat(seat);
  }

  clearSeatTooltip() {
    this.activeTooltipSeatKey = null;
  }

  isTooltipVisible(seat: SeatData | null): boolean {
    return !!seat?.seatKey && this.activeTooltipSeatKey === seat.seatKey;
  }

  isLeftEdgeSeat(seat: SeatData | null): boolean {
    if (!seat) return false;
    return Number(seat.xaxis) <= this.minSeatxaxis;
  }

  isRightEdgeSeat(seat: SeatData | null): boolean {
    if (!seat) return false;
    return Number(seat.xaxis) >= this.maxSeatxaxis;
  }

  getSeatPriceLabel(seat: SeatData | null): string {
    const finalPrice = seat?.priceDetail?.finalPrice;
    const currency = seat?.priceDetail?.currency || 'INR';
    if (typeof finalPrice !== 'number') {
      return '';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(finalPrice);
  }

  isSeatSelected(seat: SeatData | null): boolean {
    // Highlight if this seat is selected for any passenger in the current segment
    if (!seat) return false;
    return (
      this.selectedSeatsPerSegment?.[this.selectedSegmentIndex] || []
    ).some((s) => s && s.seatKey === seat.seatKey);
  }

  getSelectedSeatForPassenger(passengerIndex: number): SeatData | null {
    return (
      this.selectedSeatsPerSegment?.[this.selectedSegmentIndex]?.[
        passengerIndex
      ] || null
    );
  }

  get selectedSeatsCount(): number {
    const arr = this.selectedSeatsPerSegment?.[this.selectedSegmentIndex] || [];
    return arr.filter((s) => s).length;
  }

  getTotalPrice(): string {
    // Sum all selected seats for all passengers in the current segment
    const seats =
      this.selectedSeatsPerSegment?.[this.selectedSegmentIndex] || [];
    const total = seats.reduce(
      (sum, seat) => sum + (seat?.priceDetail?.finalPrice || 0),
      0
    );
    const currency = seats.find((seat) => seat)?.priceDetail?.currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(total);
  }

  selectTab(tab: string) {
    this.selectedTab = tab;
  }

  // continue() {
  //   if (this.selectedSeatsPerSegment?.[this.selectedSegmentIndex]?.length > 0) {
  //     this.modalController.dismiss({
  //       selectedSeats:
  //         this.selectedSeatsPerSegment?.[this.selectedSegmentIndex],
  //       price: this.getTotalPrice(),
  //     });
  //   }
  // }
  onSegmentClick(segmentIndex: number) {
    this.selectedSegmentIndex = segmentIndex;
    this.seatMap = this.seatMapBySegment[segmentIndex]?.seatMap || [];
    this.clearSeatTooltip();
    this.createSeatGrid();
  }

  // Interpolates the gradient stops into `count` evenly spaced colors so the
  // palette always spans the full gradient regardless of price-tier count.
  private generateGradientPalette(count: number): string[] {
    if (count <= 0) return [];
    const stops = this.gradientStops;
    if (count === 1) {
      const c = stops[0];
      return [`rgb(${c.r}, ${c.g}, ${c.b})`];
    }
    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
      const t = (i / (count - 1)) * (stops.length - 1);
      const idx = Math.floor(t);
      const localT = t - idx;
      const c1 = stops[idx];
      const c2 = stops[Math.min(idx + 1, stops.length - 1)];
      const r = Math.round(c1.r + (c2.r - c1.r) * localT);
      const g = Math.round(c1.g + (c2.g - c1.g) * localT);
      const b = Math.round(c1.b + (c2.b - c1.b) * localT);
      colors.push(`rgb(${r}, ${g}, ${b})`);
    }
    return colors;
  }

  // Helper to find the next passenger index (first empty, else 0)
  private getInitialNextPassengerIndex(): number {
    const seats =
      this.selectedSeatsPerSegment?.[this.selectedSegmentIndex] || [];
    const firstEmpty = seats.findIndex((s) => !s);
    return firstEmpty !== -1 ? firstEmpty : 0;
  }
}
